import React, { PropsWithChildren, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import ReactDOM from 'react-dom'
import Draggable from 'react-draggable'
import styled from 'styled-components'
import { theme } from 'styles/theme'
import { useDebouncedCallback } from 'use-debounce'

import { BoxProps } from '../Box'
import { CloseButton } from './close-button'
import { ModalContainer, ModalsContainerWrapper } from './styles'

export interface ModalProps extends Omit<BoxProps, 'color'> {
  title?: string
  visible: boolean
  width?: number
  size?: 'small' | 'normal'
  hideCloseButton?: boolean
  onClose: () => void
  draggable?: boolean
  noDragZone?: string
  customPaddingTop?: number
}

const ModalTitle = styled.h1`
  text-align: center;
  padding: 20px;
  width: 100%;
`

export const Modal: React.FC<PropsWithChildren<ModalProps>> = ({
  children,
  title,
  draggable,
  noDragZone,
  size = 'normal',
  visible,
  hideCloseButton,
  onClose,
  ...props
}) => {
  const portalNode = window.document.getElementById('modals-container')
  const ref = useRef<HTMLDivElement>(null)

  const [_visible, _setVisible] = useState(false)
  const debounced = useDebouncedCallback(_setVisible, theme.transition.duration)

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape' || e.key === 'Esc') {
        onClose()
      }
    },
    [onClose]
  )

  useEffect(() => {
    debounced(visible)

    if (visible) {
      document.addEventListener('keydown', handleKeyDown)
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [_visible, debounced, handleKeyDown, visible])

  // useOnClickOutside(ref, onClose);

  const modal = useMemo(() => {
    if (_visible || visible) {
      const modalNode = (
        <ModalContainer ref={ref} size={size} $visible={visible || _visible} {...props}>
          <div>
            {title && (
              <div>
                <ModalTitle>{title}</ModalTitle>
              </div>
            )}
            {draggable && (
              <div>
                {
                  <span style={{ cursor: 'move' }}>
                    <ModalTitle />
                  </span>
                }
              </div>
            )}
            {children}
            {!hideCloseButton && <CloseButton onClick={onClose} />}
          </div>
        </ModalContainer>
      )
      return (
        <ModalsContainerWrapper size={size}>
          {draggable ? <Draggable cancel={noDragZone}>{modalNode}</Draggable> : modalNode}
        </ModalsContainerWrapper>
      )
    }

    return null
  }, [_visible, visible, props, title, draggable, children, hideCloseButton, onClose, noDragZone])

  return portalNode ? ReactDOM.createPortal(modal, portalNode) : modal
}
